import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Box, useTheme, useMediaQuery, BottomNavigation, BottomNavigationAction } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import SchoolIcon from '@mui/icons-material/School';
import QuizIcon from '@mui/icons-material/Quiz';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import api from './api';
import { CustomThemeProvider, useTheme as useCustomTheme } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AuthModalProvider } from './contexts/AuthModalContext';
import { SafeExamModeProvider } from './contexts/SafeExamModeContext';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HighSchoolFooter from './components/HighSchoolFooter';
import ChatWidget from './components/ChatWidget/ChatWidget';
import DiscussionRoomsWidget from './components/DiscussionRoomsWidget';
import VirtualMeetingNotifications from './components/VirtualMeetingNotifications';
import LanguageHubVisitReminders from './components/LanguageHubVisitReminders';
import LoginModal from './components/modals/LoginModal';
import SignupModal from './components/modals/SignupModal';
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import Blog from './pages/Blog';
import FAQ from './pages/FAQ';
import About from './pages/About';
import Contact from './pages/Contact';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import NotFound from './pages/NotFound';
import Admin from './pages/Admin';
import { AdminTabAuthProvider } from './contexts/AdminTabAuthContext';
import BlogDetail from './pages/BlogDetail';
import Bookmarks from './pages/Bookmarks';
import AdminBlogDetail from './pages/admin/AdminBlogDetail';
import KnowledgeBase from './pages/KnowledgeBase';
import TopicPage from './pages/TopicPage';
import TopicDetail from './pages/TopicDetail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AdminLogin from './pages/AdminLogin';
import AdminSignup from './pages/AdminSignup';
import ProtectedRoute from './components/ProtectedRoute';
import GoldMemberRoute from './components/GoldMemberRoute';
import Subscription from './pages/Subscription';
import Payment from './pages/Payment';
import Sidebar from './components/Sidebar';
import AuthCallback from './pages/AuthCallback';
import WaecTopics from './pages/WaecTopics';
import JambTopics from './pages/JambTopics';
import JambMockTestStart from './pages/JambMockTestStart';
import JambSubjectSelection from './pages/JambSubjectSelection';
import JambConfirmation from './pages/JambConfirmation';
import JambTestInstructions from './pages/JambTestInstructions';
import JambTest from './pages/JambTest';
import JambResult from './pages/JambResult';
import JambCheckResult from './pages/JambCheckResult';
import WaecMockTestStart from './pages/WaecMockTestStart';
import WaecSubjectSelection from './pages/WaecSubjectSelection';
import WaecConfirmation from './pages/WaecConfirmation';
import WaecTestInstructions from './pages/WaecTestInstructions';
import WaecTest from './pages/WaecTest';
import WaecResult from './pages/WaecResult';
import HighSchoolSignup from './pages/HighSchoolSignup';
import HighSchoolLogin from './pages/HighSchoolLogin';
import HighSchoolContact from './pages/HighSchoolContact';
import HighSchoolAbout from './pages/HighSchoolAbout';
import Helpdesk from './pages/Helpdesk';
import ParentLogin from './pages/ParentLogin';
import ParentDashboard from './pages/ParentDashboard';
import ParentTickets from './pages/ParentTickets';
import ParentPayments from './pages/ParentPayments';
import ParentPaymentReceipt from './pages/ParentPaymentReceipt';
import SchoolWork from './pages/SchoolWork';
import StudentMaterials from './pages/StudentMaterials';
import StudentQuizzes from './pages/StudentQuizzes';
import StudentAssignments from './pages/StudentAssignments';
import StudentAssignmentDetail from './pages/StudentAssignmentDetail';
import StudentGrades from './pages/StudentGrades';
import StudentAttendance from './pages/StudentAttendance';
import QuizDetail from './pages/QuizDetail';
import WaecCheckResult from './pages/WaecCheckResult';
import { useSafeExamMode } from './contexts/SafeExamModeContext';
import Labs from './pages/Labs';
import OhmsLawLab from './labs/OhmsLawLab';
import MicroscopyLab from './labs/microscopy/MicroscopyLab';
import RefractionGlassBlockLab from './labs/RefractionGlassBlockLab';
import FoodTestsLab from './labs/food-tests/FoodTestsLab';
import MeasurementLab from './labs/measurement/MeasurementLab';
import ClassificationLab from './labs/classification/ClassificationLab';
import TitrationLab from './labs/titration/TitrationLab';
import PendulumLab from './labs/pendulum/PendulumLab';
import LanguageHub from './pages/LanguageHub';
import Dictionary from './pages/language-hub/Dictionary';
import Idioms from './pages/language-hub/Idioms';
import VocabularyGames from './pages/language-hub/VocabularyGames';
import GrammarLab from './pages/language-hub/GrammarLab';
import WritingStudio from './pages/language-hub/WritingStudio';
import ReadingLounge from './pages/language-hub/ReadingLounge';
import DiscussionRooms from './pages/DiscussionRooms';
import DiscussionCoordinatorLogin from './pages/DiscussionCoordinatorLogin';
import EtymologyIndex from './pages/language-hub/EtymologyIndex';
import Syllabification from './pages/language-hub/Syllabification';
import DailyChallenge from './pages/language-hub/DailyChallenge';
import { SchoolProvider } from './contexts/SchoolContext';
import { StaffProvider } from './contexts/StaffContext';
import { LanguageProvider } from './contexts/LanguageContext';
import SchoolLogin from './pages/SchoolLogin';
import SchoolSignup from './pages/SchoolSignup';
import SchoolProtectedRoute from './components/SchoolProtectedRoute';
import SchoolLayout from './components/SchoolLayout';
import SchoolDashboard from './pages/SchoolDashboard';
import SchoolBookmarks from './pages/SchoolBookmarks';
import SchoolStudents from './pages/SchoolStudents';
import SchoolStaff from './pages/SchoolStaff';
import SchoolPayments from './pages/SchoolPayments';
import SchoolProfile from './pages/SchoolProfile';
import SchoolTermResults from './pages/SchoolTermResults';
import SchoolTermHistory from './pages/SchoolTermHistory';
import SchoolParentTickets from './pages/SchoolParentTickets';
import SchoolExamTicketClasses from './pages/SchoolExamTicketClasses';
import SchoolExamTicketSettings from './pages/SchoolExamTicketSettings';
import SchoolReportCards from './pages/SchoolReportCards';
import SchoolReportCardClass from './pages/SchoolReportCardClass';
import SchoolReportCardPreview from './pages/SchoolReportCardPreview';
import SchoolTermResultStudent from './pages/SchoolTermResultStudent';
import SchoolWorkReportCard from './pages/SchoolWorkReportCard';
import StaffTermHistory from './pages/StaffTermHistory';
import SchoolWorkTermHistory from './pages/SchoolWorkTermHistory';
import StaffLogin from './pages/StaffLogin';
import StaffDashboard from './pages/StaffDashboard';
import StaffStudents from './pages/StaffStudents';
import StaffPayments from './pages/StaffPayments';
import StaffProtectedRoute from './components/StaffProtectedRoute';
import StaffLayout from './components/StaffLayout';
import StaffMaterials from './pages/StaffMaterials';
import StaffQuizzes from './pages/StaffQuizzes';
import StaffAssignments from './pages/StaffAssignments';
import StaffGrading from './pages/StaffGrading';
import StaffAttendance from './pages/StaffAttendance';
import StaffSubjects from './pages/StaffSubjects';
import StaffQuizScores from './pages/StaffQuizScores';
import StaffQuizDetail from './pages/StaffQuizDetail';
import StudentExamTicket from './pages/StudentExamTicket';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Terms from './pages/Terms';
import ChatRoom from './pages/ChatRoom';
import SchoolSearch from './pages/SchoolSearch';
import MockTests from './pages/MockTests';
import Membership from './pages/Membership';

function AppContent() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobileState, setIsMobileState] = useState(false); // Local state backup
  const { darkMode } = useCustomTheme();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isExamMode } = useSafeExamMode();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const recentSubjectMatch = location.pathname.match(/^\/(waec|jamb)\/([a-z0-9-]+)(?:\/[a-z0-9-]+){0,2}\/?$/i);
  const recentExamType = recentSubjectMatch?.[1].toUpperCase();
  const recentSubjectSlug = recentSubjectMatch?.[2];
  const recentSubjectPath = recentSubjectMatch ? location.pathname : undefined;
  const currentTab = location.pathname.startsWith('/waec') || location.pathname.startsWith('/jamb') ? 'learn' : location.pathname.startsWith('/mock-test') ? 'tests' : location.pathname.startsWith('/chat') ? 'tutor' : location.pathname.startsWith('/profile') || location.pathname.startsWith('/settings') ? 'profile' : 'home';

  // Sync isMobile to local state to handle useMediaQuery inconsistencies after multiple refreshes
  useEffect(() => {
    setIsMobileState(isMobile);
    // Always close sidebar when switching to mobile
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (isMobileState) setSidebarOpen(false);
  }, [isMobileState, location.pathname]);

  useEffect(() => {
    if (location.hash) {
      document.getElementById(decodeURIComponent(location.hash.slice(1)))?.scrollIntoView();
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (!user?._id || !recentExamType || !recentSubjectSlug) return;
    api.post('/student-dashboard/recent-subjects', { examType: recentExamType, subjectSlug: recentSubjectSlug, path: recentSubjectPath }).catch(() => undefined);
  }, [user?._id, recentExamType, recentSubjectSlug, recentSubjectPath]);

  // Apply dark background overlay in dark mode
  useEffect(() => {
    const htmlElement = document.documentElement;
    const bodyElement = document.body;
    
    if (darkMode) {
      // Add dark overlay to background image
      htmlElement.style.setProperty('--bg-overlay', 'rgba(15, 23, 42, 0.85)');
      bodyElement.style.backgroundColor = '#0f172a';
    } else {
      htmlElement.style.setProperty('--bg-overlay', 'transparent');
      bodyElement.style.backgroundColor = 'transparent';
    }
  }, [darkMode]);

  // Determine sidebar variant: temporary on mobile, persistent on larger screens (persistent can be toggled)
  const sidebarVariant = isMobileState ? 'temporary' : 'persistent';
  const isStaffRoute = location.pathname.startsWith('/staff');
  const isLandingPage = location.pathname === '/' && !user;
  let effectiveUser = user;
  if (!effectiveUser) {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) effectiveUser = JSON.parse(storedUser);
    } catch (e) {
      // Ignore invalid local auth data and use the context value.
    }
  }
  const isHighSchoolUser = effectiveUser?.educationLevel === 'high-school' ||
    Boolean(effectiveUser?.school || effectiveUser?.schoolName || effectiveUser?.schoolEmail || effectiveUser?.registrationId || effectiveUser?.className);
  const isHighSchoolRoute = isHighSchoolUser || location.pathname.startsWith('/high-school') ||
    location.pathname.startsWith('/mock-test') ||
    location.pathname.startsWith('/school-work') ||
    location.pathname.startsWith('/waec') ||
    location.pathname.startsWith('/jamb') ||
    location.pathname === '/school' ||
    location.pathname.startsWith('/school/') ||
    location.pathname === '/parent' ||
    location.pathname.startsWith('/parent/') ||
    location.pathname === '/staff' ||
    location.pathname.startsWith('/staff/');

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: darkMode ? '#0f172a' : 'transparent' }}>
        {!isExamMode && !isLandingPage && <Navbar onToggleSidebar={() => setSidebarOpen(!sidebarOpen)} />}
        {!isExamMode && !isLandingPage && <VirtualMeetingNotifications />}
        <Box sx={{ display: 'flex', flexGrow: 1 }}>
          {!isExamMode && !isStaffRoute && !isLandingPage && <Sidebar 
            open={sidebarOpen} 
            onClose={() => setSidebarOpen(false)} 
            variant={sidebarVariant}
          />}
          <Box 
            component="main" 
            sx={{ 
              flexGrow: 1, 
              pl: { 
                xs: 0, 
                sm: (sidebarOpen && !isMobileState && !isLandingPage) ? '240px' : 0,
                md: (sidebarOpen && !isMobileState && !isLandingPage) ? '240px' : 0
              }, 
                pb: !isExamMode && isHighSchoolUser && isMobileState ? '76px' : 0,
              pt: 0,
              transition: 'padding-left 0.3s ease', 
              backgroundColor: darkMode ? 'rgba(15, 23, 42, 0.3)' : 'transparent',
              width: { xs: '100%', sm: 'auto' },
              overflowX: 'hidden',
            }}
          >
                  <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/projects" element={
                  <ProtectedRoute>
                    <Projects />
                  </ProtectedRoute>
                } />
                <Route path="/projects/:id" element={
                  <ProtectedRoute>
                    <ProjectDetail />
                  </ProtectedRoute>
                } />
                <Route path="/blog" element={
                  <ProtectedRoute>
                    <Blog />
                  </ProtectedRoute>
                } />
                <Route path="/blog/:title" element={
                  <ProtectedRoute>
                    <BlogDetail />
                  </ProtectedRoute>
                } />
                <Route path="/bookmarks" element={
                  <ProtectedRoute>
                    <Bookmarks />
                  </ProtectedRoute>
                } />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/about" element={isHighSchoolRoute ? <HighSchoolAbout /> : <About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/helpdesk" element={<Helpdesk />} />
                <Route path="/labs" element={
                  <ProtectedRoute>
                    <Labs />
                  </ProtectedRoute>
                } />
                <Route path="/labs/titration" element={
                  <ProtectedRoute>
                    <TitrationLab />
                  </ProtectedRoute>
                } />
                <Route path="/labs/ohms-law" element={
                  <ProtectedRoute>
                    <OhmsLawLab />
                  </ProtectedRoute>
                } />
                <Route path="/labs/microscopy" element={
                  <ProtectedRoute>
                    <MicroscopyLab />
                  </ProtectedRoute>
                } />
                <Route path="/labs/refraction-through-glass-block" element={
                  <ProtectedRoute>
                    <RefractionGlassBlockLab />
                  </ProtectedRoute>
                } />
                <Route path="/labs/food-tests" element={
                  <ProtectedRoute>
                    <FoodTestsLab />
                  </ProtectedRoute>
                } />
                <Route path="/labs/measurement-and-units" element={
                  <ProtectedRoute>
                    <MeasurementLab />
                  </ProtectedRoute>
                } />
                <Route path="/labs/classification-of-living-organisms" element={
                  <ProtectedRoute>
                    <ClassificationLab />
                  </ProtectedRoute>
                } />
                <Route path="/labs/simple-pendulum" element={
                  <ProtectedRoute>
                    <PendulumLab />
                  </ProtectedRoute>
                } />
                <Route path="/language-hub" element={
                  <ProtectedRoute>
                    <LanguageHub />
                  </ProtectedRoute>
                } />
                <Route path="/language-hub/dictionary" element={
                  <ProtectedRoute>
                    <Dictionary />
                  </ProtectedRoute>
                } />
                <Route path="/language-hub/idioms" element={
                  <ProtectedRoute>
                    <Idioms />
                  </ProtectedRoute>
                } />
                <Route path="/language-hub/games" element={
                  <ProtectedRoute>
                    <VocabularyGames />
                  </ProtectedRoute>
                } />
                <Route path="/language-hub/grammar" element={
                  <ProtectedRoute>
                    <GrammarLab />
                  </ProtectedRoute>
                } />
                <Route path="/language-hub/writing" element={
                  <ProtectedRoute>
                    <WritingStudio />
                  </ProtectedRoute>
                } />
                <Route path="/language-hub/reading" element={
                  <ProtectedRoute>
                    <ReadingLounge />
                  </ProtectedRoute>
                } />
                <Route path="/language-hub/etymology" element={
                  <ProtectedRoute>
                    <EtymologyIndex />
                  </ProtectedRoute>
                } />
                <Route path="/language-hub/syllabification" element={
                  <ProtectedRoute>
                    <Syllabification />
                  </ProtectedRoute>
                } />
                <Route path="/language-hub/daily-challenge" element={
                  <ProtectedRoute>
                    <DailyChallenge />
                  </ProtectedRoute>
                } />
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                } />
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                } />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/knowledge-base" element={<KnowledgeBase />} />
                <Route path="/chat" element={<ChatRoom />} />
                <Route path="/school-search" element={<SchoolSearch />} />
                <Route path="/discussion-rooms/coordinator-login" element={<DiscussionCoordinatorLogin />} />
                <Route path="/discussion-rooms" element={<DiscussionRooms />} />
                
                {/* Admin routes */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/signup" element={<AdminSignup />} />
                <Route path="/admin" element={
                  <ProtectedRoute requireAdmin={true}>
                    <AdminTabAuthProvider>
                      <Admin />
                    </AdminTabAuthProvider>
                  </ProtectedRoute>
                } />
                <Route path="/admin/blogs/:title" element={
                  <ProtectedRoute requireAdmin={true}>
                    <AdminTabAuthProvider>
                      <AdminBlogDetail />
                    </AdminTabAuthProvider>
                  </ProtectedRoute>
                } />
                <Route path="/knowledge-base/:subjectSlug" element={
                  <ProtectedRoute>
                    <TopicPage />
                  </ProtectedRoute>
                } />
                <Route path="/knowledge-base/:subjectSlug/:topicSlug" element={
                  <ProtectedRoute>
                    <TopicDetail />
                  </ProtectedRoute>
                } />
                <Route path="/knowledge-base/:subjectSlug/:topicSlug/:detailSlug" element={
                  <ProtectedRoute>
                    <TopicDetail />
                  </ProtectedRoute>
                } />
                
                {/* High School (WAEC/JAMB) routes */}
                {/* Dedicated high school auth pages */}
                <Route path="/high-school/signup" element={<HighSchoolSignup />} />
                <Route path="/high-school/login" element={<HighSchoolLogin />} />
                <Route path="/high-school/about" element={<HighSchoolAbout />} />
                <Route path="/high-school/contact" element={<HighSchoolContact />} />
                <Route path="/parent/login" element={<ParentLogin />} />
                <Route path="/parent/dashboard" element={
                  <ProtectedRoute>
                    <ParentDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/parent/payments" element={
                  <ProtectedRoute>
                    <ParentPayments />
                  </ProtectedRoute>
                } />
                <Route path="/parent/payments/receipt/:paymentId" element={
                  <ProtectedRoute>
                    <ParentPaymentReceipt />
                  </ProtectedRoute>
                } />
                <Route path="/parent/tickets" element={
                  <ProtectedRoute>
                    <ParentTickets />
                  </ProtectedRoute>
                } />
                <Route path="/parent/report-card" element={
                  <ProtectedRoute>
                    <SchoolWorkReportCard />
                  </ProtectedRoute>
                } />
                <Route path="/parent" element={<Navigate to="/parent/dashboard" replace />} />
                {/* Keep a single canonical Home at '/' for high-school users; redirect legacy path to root */}
                <Route path="/high-school" element={<Navigate to="/" replace />} />
                <Route path="/waec" element={
                  <GoldMemberRoute>
                    <WaecTopics />
                  </GoldMemberRoute>
                } />
                <Route path="/jamb" element={
                  <GoldMemberRoute>
                    <JambTopics />
                  </GoldMemberRoute>
                } />
                <Route path="/waec/:sectionSlug" element={
                  <GoldMemberRoute>
                    <WaecTopics />
                  </GoldMemberRoute>
                } />
                <Route path="/jamb/:sectionSlug" element={
                  <GoldMemberRoute>
                    <JambTopics />
                  </GoldMemberRoute>
                } />
                <Route path="/waec/:sectionSlug/:topicSlug" element={
                  <GoldMemberRoute>
                    <TopicDetail />
                  </GoldMemberRoute>
                } />
                <Route path="/waec/:sectionSlug/:topicSlug/:detailSlug" element={
                  <GoldMemberRoute>
                    <TopicDetail />
                  </GoldMemberRoute>
                } />
                <Route path="/jamb/:sectionSlug/:topicSlug" element={
                  <GoldMemberRoute>
                    <TopicDetail />
                  </GoldMemberRoute>
                } />
                <Route path="/jamb/:sectionSlug/:topicSlug/:detailSlug" element={
                  <GoldMemberRoute>
                    <TopicDetail />
                  </GoldMemberRoute>
                } />

                {/* School Work Sectioned Routes */}
                <Route path="/school-work" element={
                  <ProtectedRoute>
                    <SchoolWork />
                  </ProtectedRoute>
                } />
                <Route path="/school-work/bookmarks" element={
                  <ProtectedRoute>
                    <SchoolBookmarks />
                  </ProtectedRoute>
                } />
                <Route path="/school-work/materials" element={
                  <ProtectedRoute>
                    <StudentMaterials />
                  </ProtectedRoute>
                } />
                <Route path="/school-work/quizzes" element={
                  <ProtectedRoute>
                    <StudentQuizzes />
                  </ProtectedRoute>
                } />
                <Route path="/school-work/assignments" element={
                  <ProtectedRoute>
                    <StudentAssignments />
                  </ProtectedRoute>
                } />
                <Route path="/school-work/assignments/:assignmentId" element={
                  <ProtectedRoute>
                    <StudentAssignmentDetail />
                  </ProtectedRoute>
                } />
                <Route path="/school-work/grades" element={
                  <ProtectedRoute>
                    <StudentGrades />
                  </ProtectedRoute>
                } />
                <Route path="/school-work/attendance" element={
                  <ProtectedRoute>
                    <StudentAttendance />
                  </ProtectedRoute>
                } />
                <Route path="/school-work/report-card" element={
                  <ProtectedRoute>
                    <SchoolWorkReportCard />
                  </ProtectedRoute>
                } />
                <Route path="/school-work/exam-ticket" element={
                  <ProtectedRoute>
                    <StudentExamTicket />
                  </ProtectedRoute>
                } />
                <Route path="/school-work/term-history" element={
                  <ProtectedRoute>
                    <SchoolWorkTermHistory />
                  </ProtectedRoute>
                } />
                <Route path="/school-work/quiz/:quizId" element={
                  <ProtectedRoute>
                    <QuizDetail />
                  </ProtectedRoute>
                } />
                
                {/* Mock Test Routes */}
                <Route path="/mock-tests" element={
                  <ProtectedRoute>
                    <MockTests />
                  </ProtectedRoute>
                } />
                <Route path="/membership" element={
                  <ProtectedRoute>
                    <Membership />
                  </ProtectedRoute>
                } />
                <Route path="/mock-test/jamb" element={
                  <ProtectedRoute>
                    <JambMockTestStart />
                  </ProtectedRoute>
                } />
                <Route path="/mock-test/jamb/subjects" element={
                  <ProtectedRoute>
                    <JambSubjectSelection />
                  </ProtectedRoute>
                } />
                <Route path="/mock-test/jamb/subjects/:mockTestId" element={
                  <ProtectedRoute>
                    <JambSubjectSelection />
                  </ProtectedRoute>
                } />
                <Route path="/mock-test/jamb/confirm/:mockTestId" element={
                  <ProtectedRoute>
                    <JambConfirmation />
                  </ProtectedRoute>
                } />
                <Route path="/mock-test/jamb/instructions/:mockTestId" element={
                  <ProtectedRoute>
                    <JambTestInstructions />
                  </ProtectedRoute>
                } />
                <Route path="/mock-test/jamb/test/:mockTestId" element={
                  <ProtectedRoute>
                    <JambTest />
                  </ProtectedRoute>
                } />
                <Route path="/mock-test/jamb/result/:mockTestId" element={
                  <ProtectedRoute>
                    <JambResult />
                  </ProtectedRoute>
                } />
                <Route path="/mock-test/jamb/check-result" element={
                  <ProtectedRoute>
                    <JambCheckResult />
                  </ProtectedRoute>
                } />
                <Route path="/mock-test/waec" element={
                  <ProtectedRoute>
                    <WaecMockTestStart />
                  </ProtectedRoute>
                } />
                <Route path="/mock-test/waec/subjects" element={
                  <ProtectedRoute>
                    <WaecSubjectSelection />
                  </ProtectedRoute>
                } />
                <Route path="/mock-test/waec/subjects/:mockTestId" element={
                  <ProtectedRoute>
                    <WaecSubjectSelection />
                  </ProtectedRoute>
                } />
                <Route path="/mock-test/waec/confirm/:mockTestId" element={
                  <ProtectedRoute>
                    <WaecConfirmation />
                  </ProtectedRoute>
                } />
                <Route path="/mock-test/waec/instructions/:mockTestId" element={
                  <ProtectedRoute>
                    <WaecTestInstructions />
                  </ProtectedRoute>
                } />
                <Route path="/mock-test/waec/test/:mockTestId" element={
                  <ProtectedRoute>
                    <WaecTest />
                  </ProtectedRoute>
                } />
                <Route path="/mock-test/waec/result/:mockTestId" element={
                  <ProtectedRoute>
                    <WaecResult />
                  </ProtectedRoute>
                } />
                <Route path="/mock-test/waec/check-result" element={
                  <ProtectedRoute>
                    <WaecCheckResult />
                  </ProtectedRoute>
                } />
                
                {/* JAMB Mock Test Routes */}
                <Route path="/subscription" element={<Subscription />} />
                <Route path="/payment" element={<Payment />} />
                <Route path="/auth-callback" element={<AuthCallback />} />
                
                {/* School Management Routes */}
                <Route path="/school/login" element={<SchoolLogin />} />
                <Route path="/school/signup" element={<SchoolSignup />} />
                <Route path="/school/dashboard" element={
                  <SchoolProtectedRoute>
                    <SchoolLayout>
                      <SchoolDashboard />
                    </SchoolLayout>
                  </SchoolProtectedRoute>
                } />
                <Route path="/school/students" element={
                  <SchoolProtectedRoute>
                    <SchoolLayout>
                      <SchoolStudents />
                    </SchoolLayout>
                  </SchoolProtectedRoute>
                } />
                <Route path="/school/staff" element={
                  <SchoolProtectedRoute>
                    <SchoolLayout>
                      <SchoolStaff />
                    </SchoolLayout>
                  </SchoolProtectedRoute>
                } />
                <Route path="/school/payments" element={
                  <SchoolProtectedRoute>
                    <SchoolLayout>
                      <SchoolPayments />
                    </SchoolLayout>
                  </SchoolProtectedRoute>
                } />
                <Route path="/school/profile" element={
                  <SchoolProtectedRoute>
                    <SchoolLayout>
                      <SchoolProfile />
                    </SchoolLayout>
                  </SchoolProtectedRoute>
                } />
                <Route path="/school/exam-ticket-settings" element={
                  <SchoolProtectedRoute>
                    <SchoolLayout>
                      <SchoolExamTicketClasses />
                    </SchoolLayout>
                  </SchoolProtectedRoute>
                } />
                <Route path="/school/exam-ticket-settings/:className" element={
                  <SchoolProtectedRoute>
                    <SchoolLayout>
                      <SchoolExamTicketSettings />
                    </SchoolLayout>
                  </SchoolProtectedRoute>
                } />
                <Route path="/school/term-results" element={
                  <SchoolProtectedRoute>
                    <SchoolLayout>
                      <SchoolTermResults />
                    </SchoolLayout>
                  </SchoolProtectedRoute>
                } />
                <Route path="/school/report-cards" element={
                  <SchoolProtectedRoute>
                    <SchoolLayout>
                      <SchoolReportCards />
                    </SchoolLayout>
                  </SchoolProtectedRoute>
                } />
                <Route path="/school/report-cards/class/:className" element={
                  <SchoolProtectedRoute>
                    <SchoolLayout>
                      <SchoolReportCardClass />
                    </SchoolLayout>
                  </SchoolProtectedRoute>
                } />
                <Route path="/school/report-cards/student/:studentId" element={
                  <SchoolProtectedRoute>
                    <SchoolLayout>
                      <SchoolReportCardPreview />
                    </SchoolLayout>
                  </SchoolProtectedRoute>
                } />
                <Route path="/school/parent-tickets" element={
                  <SchoolProtectedRoute>
                    <SchoolLayout>
                      <SchoolParentTickets />
                    </SchoolLayout>
                  </SchoolProtectedRoute>
                } />
                <Route path="/school/term-history" element={
                  <SchoolProtectedRoute>
                    <SchoolLayout>
                      <SchoolTermHistory />
                    </SchoolLayout>
                  </SchoolProtectedRoute>
                } />
                <Route path="/school/term-results/student/:studentId" element={
                  <SchoolProtectedRoute>
                    <SchoolLayout>
                      <SchoolTermResultStudent />
                    </SchoolLayout>
                  </SchoolProtectedRoute>
                } />

                {/* Staff portal routes */}
                <Route path="/staff/login" element={<StaffLogin />} />
                <Route path="/staff" element={<Navigate to="/staff/dashboard" replace />} />
                <Route path="/staff/dashboard" element={
                  <StaffProtectedRoute>
                    <StaffLayout>
                      <StaffDashboard />
                    </StaffLayout>
                  </StaffProtectedRoute>
                } />
                <Route path="/staff/students" element={
                  <StaffProtectedRoute permission="manage_students">
                    <StaffLayout>
                      <StaffStudents />
                    </StaffLayout>
                  </StaffProtectedRoute>
                } />
                <Route path="/staff/term-history" element={
                  <StaffProtectedRoute permission="manage_students">
                    <StaffLayout>
                      <StaffTermHistory />
                    </StaffLayout>
                  </StaffProtectedRoute>
                } />
                <Route path="/staff/payments" element={
                  <StaffProtectedRoute permission="view_payments">
                    <StaffLayout>
                      <StaffPayments />
                    </StaffLayout>
                  </StaffProtectedRoute>
                } />
                <Route path="/staff/materials" element={
                  <StaffProtectedRoute>
                    <StaffLayout>
                      <StaffMaterials />
                    </StaffLayout>
                  </StaffProtectedRoute>
                } />
                <Route path="/staff/subjects" element={
                  <StaffProtectedRoute>
                    <StaffLayout>
                      <StaffSubjects />
                    </StaffLayout>
                  </StaffProtectedRoute>
                } />
                <Route path="/staff/quizzes" element={
                  <StaffProtectedRoute>
                    <StaffLayout>
                      <StaffQuizzes />
                    </StaffLayout>
                  </StaffProtectedRoute>
                } />
                <Route path="/staff/assignments" element={
                  <StaffProtectedRoute>
                    <StaffLayout>
                      <StaffAssignments />
                    </StaffLayout>
                  </StaffProtectedRoute>
                } />
                <Route path="/staff/grading" element={
                  <StaffProtectedRoute>
                    <StaffLayout>
                      <StaffGrading />
                    </StaffLayout>
                  </StaffProtectedRoute>
                } />
                <Route path="/staff/grading/class/:className" element={
                  <StaffProtectedRoute>
                    <StaffLayout>
                      <StaffGrading />
                    </StaffLayout>
                  </StaffProtectedRoute>
                } />
                <Route path="/staff/grading/class/:className/student/:studentId" element={
                  <StaffProtectedRoute>
                    <StaffLayout>
                      <StaffGrading />
                    </StaffLayout>
                  </StaffProtectedRoute>
                } />
                <Route path="/staff/attendance" element={
                  <StaffProtectedRoute>
                    <StaffLayout>
                      <StaffAttendance />
                    </StaffLayout>
                  </StaffProtectedRoute>
                } />
                <Route path="/staff/quiz-scores" element={
                  <StaffProtectedRoute>
                    <StaffLayout>
                      <StaffQuizScores />
                    </StaffLayout>
                  </StaffProtectedRoute>
                } />
                <Route path="/staff/quiz-scores/:quizId/:date" element={<StaffProtectedRoute><StaffQuizDetail /></StaffProtectedRoute>} />
                
                <Route path="*" element={<NotFound />} />
                </Routes>
                {!isExamMode && !isStaffRoute && !isLandingPage && isHighSchoolUser && isMobileState && (
                  <Box sx={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 1300, backgroundColor: 'background.paper', borderTop: '1px solid', borderColor: 'divider', boxShadow: '0 -8px 18px rgba(15, 23, 42, 0.08)' }}>
                    <BottomNavigation
                      value={currentTab}
                      onChange={(_, value) => {
                        if (!value) return;
                        setSidebarOpen(false);
                        const destinations: Record<string, string> = {
                          home: '/',
                          learn: '/waec',
                          tests: '/mock-tests',
                          tutor: '/chat',
                          profile: '/profile',
                        };
                        navigate(destinations[String(value)] || '/');
                      }}
                      showLabels
                      sx={{ height: 68 }}
                    >
                      <BottomNavigationAction label="Home" value="home" icon={<HomeIcon />} />
                      <BottomNavigationAction label="Learn" value="learn" icon={<SchoolIcon />} />
                      <BottomNavigationAction label="Mock Tests" value="tests" icon={<QuizIcon />} />
                      <BottomNavigationAction label="AI Tutor" value="tutor" icon={<ChatBubbleOutlineIcon />} />
                      <BottomNavigationAction label="Profile" value="profile" icon={<PersonOutlineIcon />} />
                    </BottomNavigation>
                  </Box>
                )}
            </Box>
          </Box>
        </Box>
        {!isExamMode && !isLandingPage && (
          <Box sx={{
            pl: { xs: 0, sm: sidebarOpen && !isStaffRoute ? '240px' : 0 },
            pb: !isStaffRoute && isHighSchoolUser && isMobileState ? 'calc(76px + env(safe-area-inset-bottom, 0px))' : 0,
            transition: 'padding-left 0.3s ease',
          }}>
            {isHighSchoolRoute ? <HighSchoolFooter /> : <Footer />}
          </Box>
        )}
      <LanguageHubVisitReminders userId={user?._id} />
      {/* Auth modals */}
      <LoginModal />
      <SignupModal />
    </>
  );
}

function App() {
  return (
    <CustomThemeProvider>
      <LanguageProvider>
        <Router>
          <AuthProvider>
            <AuthModalProvider>
              <SafeExamModeProvider>
                <SchoolProvider>
                  <StaffProvider>
                    <AppContent />
                    {/* Global chat widget wrapper */}
                    <ChatWidgetWrapper />
                    <DiscussionRoomsWidgetWrapper />
                  </StaffProvider>
                </SchoolProvider>
              </SafeExamModeProvider>
            </AuthModalProvider>
          </AuthProvider>
        </Router>
      </LanguageProvider>
    </CustomThemeProvider>
  );
}

// Wrapper to conditionally show ChatWidget
function ChatWidgetWrapper() {
  const { isExamMode } = useSafeExamMode();
  return !isExamMode ? <ChatWidget /> : null;
}

function DiscussionRoomsWidgetWrapper() {
  const { isExamMode } = useSafeExamMode();
  return !isExamMode ? <DiscussionRoomsWidget /> : null;
}

export default App;